import { useState } from "react";
import { Play, ShieldCheck, Flame, Brain } from "lucide-react";
import { blindsFor } from "@/poker/engine";
import { fmt } from "@/poker/cards";

const SKILLS = [
  { v: "novice", label: "Novice", d: "Loose & passive, calls too much, rarely bluffs" },
  { v: "regular", label: "Regular", d: "Tight-aggressive, respects bets, push/fold when short" },
  { v: "pro", label: "Pro", d: "Positional, pot-odds driven, balanced bluffs & semi-bluffs" },
  { v: "mixed", label: "Mixed", d: "Each opponent is randomly Novice, Regular or Pro" },
  { v: "proplus", label: "Pro+", d: "Range-based tournament crusher: balanced 3-bet/4-bet bluffs, smart BB defense, stack-depth ranges, texture-aware sizing, floats & river pressure", hard: true, badge: "Stronger than Pro" },
  { v: "proplusplus", label: "Pro++", d: "Hardest. Tracks every player's live stats (VPIP, 3-bet, fold-to-c-bet, aggression, showdowns) and exploits your leaks from a solid balanced base", hard: true, badge: "Hardest · Adapts to you", top: true },
];
const STACKS = [1500, 3000, 5000, 10000];
const BLINDS = [[10, 20], [15, 30], [25, 50], [50, 100], [100, 200]];
const SPEEDS = [5, 8, 10, 15, 20];
const FELTS = [["emerald", "Emerald"], ["midnight", "Midnight"], ["crimson", "Crimson"]];

const Pill = ({ active, onClick, children, testId, disabled }) => (
  <button type="button" data-testid={testId} onClick={onClick} disabled={disabled}
    className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${active ? "bg-amber-500 border-amber-500 text-black" : "bg-white/5 border-white/10 text-slate-300 hover:border-amber-500/50"} disabled:opacity-30 disabled:pointer-events-none`}>
    {children}
  </button>
);

const Field = ({ label, hint, children }) => (
  <div className="space-y-3">
    <div className="flex items-baseline justify-between gap-4">
      <span className="font-heading text-lg uppercase tracking-wide text-slate-100">{label}</span>
      {hint && <span className="text-xs text-slate-500">{hint}</span>}
    </div>
    {children}
  </div>
);

export const SetupScreen = ({ onStart, felt, setFelt }) => {
  const [numPlayers, setNum] = useState(6);
  const [aiSkill, setSkill] = useState("regular");
  const [stack, setStack] = useState(3000);
  const [blind, setBlind] = useState(1);
  const [hpl, setHpl] = useState(10);
  const bb = BLINDS[blind][1];
  const cfg = { numPlayers, aiSkill, startingStack: stack, bigBlind: bb, handsPerLevel: hpl };
  const lvl2 = blindsFor(cfg, 1);

  return (
    <div className="min-h-screen setup-bg px-5 sm:px-10 lg:px-16 py-10 sm:py-16">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20">
        <div className="space-y-6 fade-up">
          <span className="text-xs uppercase tracking-[0.3em] text-amber-400">No-Limit Texas Hold'em</span>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[0.9] tracking-tight text-white">
            Tournament<br /><span className="text-amber-400">Trainer</span>
          </h1>
          <p className="text-slate-400 max-w-md leading-relaxed">
            Sit down at a full table against AI opponents. Blinds climb, stacks shrink, and every decision counts. Get a full breakdown of your play when the tournament ends.
          </p>
          <div className="flex items-start gap-3 max-w-md p-4 rounded-xl bg-white/[0.03] border border-white/10">
            <ShieldCheck className="text-emerald-400 shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-slate-400">Fair deals from a cryptographically shuffled deck. AI opponents only see their own cards and the board — no peeking.</p>
          </div>
        </div>

        <div data-testid="setup-form" className="space-y-8 p-6 sm:p-8 rounded-2xl bg-[#12141C]/90 border border-white/10 backdrop-blur-xl fade-up" style={{ animationDelay: "120ms" }}>
          <Field label="Players" hint="You + AI opponents">
            <div data-testid="player-count-select" className="flex flex-wrap gap-2">
              {[2, 3, 4, 5, 6, 7, 8, 9].map((n) => <Pill key={n} testId={`player-count-${n}`} active={numPlayers === n} onClick={() => setNum(n)}>{n}</Pill>)}
            </div>
          </Field>
          <Field label="AI Strength">
            <div data-testid="ai-strength-select" className="grid sm:grid-cols-2 gap-2">
              {SKILLS.map((s) => {
                const on = aiSkill === s.v;
                const tone = s.top
                  ? on ? "border-fuchsia-500 bg-fuchsia-500/15 shadow-[0_0_28px_rgba(217,70,239,0.3)]" : "border-fuchsia-500/40 bg-fuchsia-500/[0.06] hover:border-fuchsia-500/70"
                  : s.hard
                  ? on ? "border-red-500 bg-red-500/15 shadow-[0_0_24px_rgba(239,68,68,0.25)]" : "border-red-500/40 bg-red-500/[0.06] hover:border-red-500/70"
                  : on ? "border-amber-500 bg-amber-500/10" : "border-white/10 bg-white/[0.03] hover:border-white/25";
                return (
                  <button key={s.v} type="button" data-testid={`ai-strength-${s.v}`} onClick={() => setSkill(s.v)}
                    className={`text-left p-3 rounded-xl border transition-[border-color,background-color,box-shadow] ${s.hard ? "sm:col-span-2" : ""} ${tone}`}>
                    <div className="flex items-center gap-2">
                      <span className={`font-heading text-lg uppercase ${s.top ? "text-fuchsia-400" : s.hard ? "text-red-400" : on ? "text-amber-400" : "text-slate-100"}`}>{s.label}</span>
                      {s.hard && <span data-testid={`${s.v}-hardest-badge`} className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-white text-[10px] font-bold uppercase tracking-wider ${s.top ? "bg-fuchsia-600" : "bg-red-600"}`}>{s.top ? <Brain size={11} /> : <Flame size={11} />}{s.badge}</span>}
                    </div>
                    <div className="text-xs text-slate-500">{s.d}</div>
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Starting Stack" hint={`${Math.round(stack / bb)} big blinds`}>
            <div className="flex flex-wrap gap-2 items-center">
              {STACKS.map((s) => <Pill key={s} testId={`stack-preset-${s}`} disabled={s < bb * 10} active={stack === s} onClick={() => setStack(s)}>{fmt(s)}</Pill>)}
              <input data-testid="starting-stack-input" type="number" min={bb * 10} step={100} value={stack}
                onChange={(e) => setStack(Math.max(0, Number(e.target.value)))}
                onBlur={() => setStack((s) => Math.max(bb * 10, s))}
                className="w-28 h-10 rounded-full bg-black/40 border border-white/10 px-4 font-mono text-sm text-amber-300 focus:outline-none focus:border-amber-400" />
            </div>
          </Field>
          <Field label="Starting Blinds">
            <div data-testid="starting-blinds-select" className="flex flex-wrap gap-2">
              {BLINDS.map(([s, b], i) => <Pill key={b} testId={`blinds-${s}-${b}`} active={blind === i} onClick={() => { setBlind(i); setStack((st) => (st < b * 10 ? STACKS.find((x) => x >= b * 10) || b * 10 : st)); }}>{s}/{b}</Pill>)}
            </div>
          </Field>
          <Field label="Hands per Level" hint={`Then blinds go to ${fmt(lvl2.sb)}/${fmt(lvl2.bb)}`}>
            <div className="flex flex-wrap gap-2 items-center">
              {SPEEDS.map((h) => <Pill key={h} testId={`hpl-${h}`} active={hpl === h} onClick={() => setHpl(h)}>{h}</Pill>)}
              <input data-testid="hands-per-level-input" type="number" min={1} max={100} value={hpl}
                onChange={(e) => setHpl(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
                className="w-20 h-10 rounded-full bg-black/40 border border-white/10 px-4 font-mono text-sm text-amber-300 focus:outline-none focus:border-amber-400" />
            </div>
          </Field>
          <Field label="Felt">
            <div className="flex gap-2">
              {FELTS.map(([v, l]) => <Pill key={v} testId={`felt-${v}`} active={felt === v} onClick={() => setFelt(v)}>{l}</Pill>)}
            </div>
          </Field>
          <button data-testid="start-tournament-button" onClick={() => onStart({ ...cfg, startingStack: Math.max(bb * 10, stack) })}
            className="w-full h-14 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-heading text-2xl font-extrabold uppercase tracking-wide flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(245,158,11,0.35)] transition-[background-color,transform] hover:-translate-y-0.5">
            <Play size={20} fill="currentColor" /> Start Tournament
          </button>
        </div>
      </div>
    </div>
  );
};
